// The pull half of the safe-area contract with MainActivity.java.
//
// The native side pushes --android-inset-* onto <html> whenever the window
// insets change, but the first (and on a cold start, only) dispatch happens
// before Capacitor has loaded index.html — so the properties land on a
// document that is then thrown away. Nothing re-pushes them, --safe-bottom
// stays 0, and the bottom tab bar ends up under the navigation buttons.
//
// So the page asks, once it exists. window.AndroidInsets is added with
// addJavascriptInterface, which survives page loads; the push is still what
// keeps rotation and navigation-mode changes live.

const SIDES = ["top", "right", "bottom", "left"] as const

declare global {
  interface Window {
    // Present only inside the native Android shell.
    AndroidInsets?: { get(): string | null }
  }
}

/**
 * Copy the native window insets onto <html> as --android-inset-*. A no-op
 * everywhere except the Android shell, and safe to call repeatedly.
 */
export function applyAndroidInsets(): void {
  const raw = window.AndroidInsets?.get()
  if (!raw) return

  const values = raw.split(",")
  if (values.length !== SIDES.length) return

  const style = document.documentElement.style
  SIDES.forEach((side, index) => {
    const px = Number.parseFloat(values[index])
    // Ignore a malformed side rather than writing "NaNpx", which would make
    // max() invalid and collapse the whole token to nothing.
    if (Number.isFinite(px)) style.setProperty(`--android-inset-${side}`, `${px}px`)
  })
}

/**
 * Apply them now and after any resize. The native listener already re-pushes
 * on rotation, but a resize is also what a navigation-mode change (gesture bar
 * ⇄ three buttons) looks like from here, and re-reading a cached string costs
 * nothing.
 */
export function installAndroidInsets(): void {
  if (!window.AndroidInsets) return
  applyAndroidInsets()
  window.addEventListener("resize", applyAndroidInsets)
  window.addEventListener("orientationchange", applyAndroidInsets)
}
